import { Box, IconButton } from "@mui/material";
import { useInputFieldAddEditDialog } from "./InputFieldAddEditDialog.hooks.ts";
import { DotLegalAlert, DotLegalDialog, DotLegalHeader, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { isNullOrWhitespace } from "../../../common/stringOperations.ts";
import { useCreateInputTypeFilterOptions } from "../InputFields.hooks.ts";
import CloseIcon from "@mui/icons-material/Close";
import { InputFieldTypeEnum } from "../InputFields.types.ts";
import { Trans } from "react-i18next";
import DotLegalLink from "../../../common/components/dotLegalLink/DotLegalLink.tsx";
import React from "react";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { TemplateEntityType } from "../../EntityTemplate.types.ts";

export interface InputFieldAddEditDialogProps {
    inputFieldIdToEdit?: string;
    templateEntityType: TemplateEntityType;
    onDialogClose: () => void;
    onSaved: () => void;
}

function InputFieldAddEditDialog(props: InputFieldAddEditDialogProps) {
    const inputFieldTypes = useCreateInputTypeFilterOptions();
    const isEdit = !isNullOrWhitespace(props.inputFieldIdToEdit);
    const { getInputTypeHelpCenterUrl } = useUrlProvider();

    const { translateString } = useTranslation();
    const { isLoading, lastAddedOptionId, saveModel, modelUpdater, showError, inProgress, onSaveInputField } = useInputFieldAddEditDialog(props);

    return (
        <DotLegalDialog
            size="sm"
            inProgress={inProgress}
            header={isEdit ? translateString("editInputField") : translateString("createInputField")}
            buttonOkText={isEdit ? translateString("save") : translateString("create")}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={onSaveInputField}
            isLoading={isLoading}
        >
            <Box sx={{ paddingX: 4 }}>
                <DotLegalTextField
                    label={translateString("name")}
                    value={saveModel.name}
                    debounce={false}
                    errorText={isNullOrWhitespace(saveModel.name) && showError ? translateString("nameRequired") : undefined}
                    onChange={modelUpdater.onNameChange}
                />

                <DotLegalTextField
                    label={translateString("description")}
                    value={saveModel.description ?? null}
                    debounce={false}
                    onChange={modelUpdater.onDescriptionChange}
                    multiline
                    rows={3}
                />

                <DotLegalSelect
                    options={inputFieldTypes}
                    selectedItem={saveModel.type.toString()}
                    onChange={(type) => modelUpdater.onFieldTypeChange(type!)}
                    label={translateString("inputType")}
                    noOptionsLabel={""}
                    disableClearable
                    disabled={isEdit}
                />

                {(saveModel.type === InputFieldTypeEnum.SingleDropDown || saveModel.type === InputFieldTypeEnum.MultiDropDown) && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <DotLegalHeader headerStyle={"small"} marginTop={2} marginBottom={1}>
                            {translateString("options")}
                        </DotLegalHeader>
                        <Box>
                            {saveModel.selectOptions?.map((x, index) => {
                                return (
                                    <Box
                                        key={x.id + index}
                                        sx={(theme) => ({
                                            width: "100%",
                                            display: "flex",
                                            "&:hover": {
                                                backgroundColor: theme.palette.primary.light,
                                            },
                                        })}
                                    >
                                        <Box
                                            sx={{
                                                flex: 1,
                                                "& .MuiInputBase-root .MuiInputBase-input": {
                                                    paddingTop: "4px",
                                                },
                                            }}
                                        >
                                            <DotLegalTextField
                                                size="small"
                                                variant="standard"
                                                noMargin
                                                label={""}
                                                value={x.name}
                                                placeholder={translateString("option")}
                                                debounce
                                                onChange={(value) => modelUpdater.onSelectOptionChange(x.id, value)}
                                                autoFocus={lastAddedOptionId === x.id}
                                                errorText={showError && isNullOrWhitespace(x.name) ? translateString("fieldRequired") : undefined}
                                            />
                                        </Box>

                                        {(saveModel.selectOptions?.length ?? 0) > 1 && (
                                            <IconButton className="deleteAnswerOption" size="small" onClick={() => modelUpdater.onRemoveSelectOption(x.id)}>
                                                <CloseIcon sx={(theme) => ({ fill: theme.palette.error.main })} />
                                            </IconButton>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Box
                                sx={(theme) => ({
                                    "&:hover": { cursor: "pointer", textDecoration: "underline" },
                                    color: theme.palette.primary.main,
                                    fontSize: 12,
                                })}
                                onClick={() => modelUpdater.addSelectOption()}
                            >
                                {translateString("addOption")}
                            </Box>
                            {showError && (saveModel.selectOptions === undefined || saveModel.selectOptions?.length === 0) && (
                                <Box sx={(theme) => ({ color: theme.palette.error.main, fontSize: 12 })}>- {translateString("oneSelectOptionIsRequired")}</Box>
                            )}
                        </Box>

                        {isEdit && <DotLegalAlert marginTop={1} warningText={<Trans i18nKey={"inputFieldSelectOptionWarning"} />} />}
                    </Box>
                )}

                <Box sx={{ display: "flex", justifyContent: "center", mt: "20px", mb: "-20px" }}>
                    <DotLegalHeader headerStyle={"extraSmall"} marginBottom={0} fontWeight={400}>
                        <Trans
                            i18nKey="learnMoreAboutInputTypesLink"
                            components={{
                                inputTypeLink: (
                                    <DotLegalLink linkColor={"primary"} to={getInputTypeHelpCenterUrl()}>
                                        here
                                    </DotLegalLink>
                                ),
                            }}
                        />
                    </DotLegalHeader>
                </Box>
            </Box>
        </DotLegalDialog>
    );
}

export default InputFieldAddEditDialog;
